import { useEffect } from 'react';
import type { Player } from '../types/player';

interface RecentPick {
  pick: number;
  teamId: number;
  playerName: string;
  position: string;
  team: string;
  timestamp: Date;
}

interface UseCpuDraftingProps {
  draftStatus: 'inactive' | 'starting' | 'active' | 'ending';
  isUserTurn: boolean;
  availablePlayers: Player[];
  currentPick: number;
  currentTeam: number;
  round: number;
  teamCount: number;
  onCpuPick: (player: Player) => void;
}

export function useCpuDrafting({
  draftStatus,
  isUserTurn,
  availablePlayers,
  currentPick,
  currentTeam,
  round,
  teamCount,
  onCpuPick
}: UseCpuDraftingProps) {
  useEffect(() => {
    // Only run CPU picking logic during active draft
    if (draftStatus !== 'active') return;
    
    // Only run if it's not user's turn and we have available players
    if (isUserTurn || availablePlayers.length === 0) return;

    // Don't draft if we've completed all rounds (assume 16 rounds max)
    const maxPicks = teamCount * 16;
    if (currentPick > maxPicks) return;

    console.log(`CPU Turn - Pick ${currentPick}, Team ${currentTeam}, Round ${round}`);

    // CPU needs time to "think" - but not too long or users get bored
    // Optimal timing: 2-4 seconds (enough to read the pick, not too long to get bored)
    const baseDelay = 450;
    const randomDelay = Math.random() * 400;
    const finalDelay = baseDelay + randomDelay;

    console.log(`CPU will pick in ${finalDelay}ms`);

    const cpuPickTimer = setTimeout(() => {
      console.log(`CPU making pick for team ${currentTeam}...`);

      // Get best available player for CPU
      const bestAvailable = availablePlayers
        .filter(p => p.rank && p.rank <= 200) // Only consider reasonable draft picks
        .sort((a, b) => (a.rank || 999) - (b.rank || 999));

      if (bestAvailable.length > 0) {
        // CPU picks from top available with slight randomness for realism
        const topPlayers = bestAvailable.slice(0, 5); // Consider top 5 players
        const randomIndex = Math.floor(Math.random() * Math.min(3, topPlayers.length)); // Pick from top 3 usually
        const selectedPlayer = topPlayers[randomIndex] || bestAvailable[0];

        console.log(`CPU picked: ${selectedPlayer.name} (${selectedPlayer.position}) - Rank ${selectedPlayer.rank}`);

        // Make the pick for CPU (this will handle recent picks and advancing automatically)
        onCpuPick(selectedPlayer);
      }
    }, finalDelay);

    return () => clearTimeout(cpuPickTimer);
  }, [
    draftStatus,
    isUserTurn,
    availablePlayers,
    currentPick,
    currentTeam,
    round,
    teamCount,
    onCpuPick
  ]);
}